import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faCheck, faLock } from '@fortawesome/pro-solid-svg-icons';
import { useIntl } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import type { DailyActionTaskReward_anyRewardConfig } from './__generated__/index.graphql.ts';

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  20% {
    transform: scale(0.9);
  }
  40% {
    transform: scale(1);
  }
`;

const borderPulseAnimation = keyframes`
  30% {
    transform: scale(1);
    opacity: 1;

  }
  80% {
    transform: scale(1.5);
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
`;

const Root = styled(Vertical)`
  &.claimable,
  &.claiming {
    animation: ${pulseAnimation} 2s ease-in-out infinite;
  }
`;

const RewardBlock = styled.div`
  padding: var(--half-unit);
  border-radius: var(--half-unit);
  background: var(--c-nd-100-opaque);
  position: relative;
  .claimable &,
  .claiming & {
    cursor: pointer;
    border: 1px solid var(--c-nd-150);
    background: var(--c-blue-400);
    box-shadow: 0 0 4px 0 var(--c-blue-400);
    &:after {
      content: '';
      position: absolute;
      inset: 0;
      border: 1px solid var(--c-blue-400);
      border-radius: var(--half-unit);
      animation: ${borderPulseAnimation} 2s ease-in-out infinite;
    }
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 2px;
  right: 2px;
  & > * {
    height: 14px;
  }
`;

type Props = {
  state: 'claimed' | 'claiming' | 'claimable' | 'locked';
  anyRewardConfig: DailyActionTaskReward_anyRewardConfig;
  onClaim: () => void;
};

const Icon = ({
  state,
}: {
  state: 'claimed' | 'claiming' | 'claimable' | 'locked';
}) => {
  if (state === 'claimed') {
    return <FontAwesomeIcon icon={faCheck} size="xs" />;
  }
  if (state === 'claiming') {
    return <LoadingIndicator smaller />;
  }
  if (state === 'locked') {
    return <FontAwesomeIcon icon={faLock} size="xs" />;
  }
  return null;
};
export const DailyActionTaskReward = ({
  state,
  anyRewardConfig,
  onClaim,
}: Props) => {
  const { formatMessage } = useIntl();
  if (!isType(anyRewardConfig, 'InGameCurrencyRewardConfig')) {
    return null;
  }
  return (
    <Root center gap={0.5} className={state}>
      <RewardBlock
        {...(state === 'claimable' ? { onClick: onClaim, role: 'button' } : {})}
      >
        <RewardConfigImage
          anyRewardConfig={anyRewardConfig}
          alt={
            state === 'locked'
              ? formatMessage(glossary.locked)
              : formatMessage(glossary.claimed)
          }
        />
        <IconWrapper>
          <Icon state={state} />
        </IconWrapper>
      </RewardBlock>
      {state !== 'claimed' && <LabelS bold>x{anyRewardConfig.amount}</LabelS>}
    </Root>
  );
};

DailyActionTaskReward.fragments = {
  anyRewardConfig: gql`
    fragment DailyActionTaskReward_anyRewardConfig on AnyRewardConfigInterface {
      id
      ... on InGameCurrencyRewardConfig {
        id
        amount
        currency
      }
      ...RewardConfigImage_anyRewardConfigInterface
    }
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<DailyActionTaskReward_anyRewardConfig>,
};
